import React, { Component, PropTypes } from 'react';
import classNames from 'classnames';
import ga from 'react-ga';
import { CargoType } from 'types';
import ButtonControl from 'components/ButtonControl';
import InputControl from 'components/InputControl';
import CargosStore from 'stores/CargosStore';
import ActionCreators from 'actions/ActionCreators';
import { translate as t } from 'lang';
import Regexps from 'constants/Regexps';
import { CategoryType } from 'types';

import './TotalMeasurementsPaneControl.scss';

const TEXT = {
    WEIGHT: t('WEIGHT'),
    VOLUME: t('VOLUME'),
    TOTAL_WEIGHT: t('TOTAL_WEIGHT'),
    TOTAL_VOLUME: t('TOTAL_VOLUME'),
    SAVE: t('SAVE'),
    CANCEL: t('CANCEL'),
}

function getStateFromStores() {
    return {
        totalMeasurements: CargosStore.getTotalMeasurements(),
    }
}

class TotalMeasurementsPaneControl extends Component {

    static propTypes = {
        selectedCategory: PropTypes.instanceOf(CategoryType).isRequired,
    }

    constructor(props) {
        super(props);
        var storeStates = this._normalizeTotalMeasurements(getStateFromStores());

        this.state = {
            ...storeStates,
        }
    }

    componentDidMount() {
        CargosStore.addChangeListener(this._onChange);
    }

    componentWillUnmount() {
        CargosStore.removeChangeListener(this._onChange);
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (nextState.totalMeasurements !== this.state.totalMeasurements
            || nextState.totalWeight !== this.state.totalWeight
            || nextState.totalVolume !== this.state.totalVolume
            || nextState.weightMeasure !== this.state.weightMeasure) {
            return true;
        }

        return false;
    }

    _onChange = () => {
        const storeStates = getStateFromStores();

        this.setState(this._normalizeTotalMeasurements(storeStates));
    }

    _initTotalVolumeInputRef = (DOMNode) => {
        this.totalVolumeInput = DOMNode;
    }

    _normalizeTotalMeasurements = (storeStates) => {
        let weightMeasure = TEXT.WEIGHT[0]; // totalWeight in CargoStore is always kept in kg
        let totalWeight = storeStates.totalMeasurements.get('total_weight');
        let totalVolume = storeStates.totalMeasurements.get('total_volume');

        if (totalWeight >= 1000) { // is equal or more then 1 ton
            weightMeasure = TEXT.WEIGHT[1];
            totalWeight /= 1000; // kg => t
        }

        totalWeight = Math.round(totalWeight * 100) / 100; // toFixed(2 or less)
        totalVolume = Math.round(totalVolume * 100) / 100; // toFixed(2 or less)

        return (Object.assign(
            storeStates,
            {
                'weightMeasure': weightMeasure,
                'totalWeight': totalWeight,
                'totalVolume': totalVolume,
            }
        )
        );
    }

    _onTotalWeightChange = (event) => {
        let value = event.target.value;
        value = value.replace(/,/g, ".");

        if (this._validateFloat(value)) {
            this.setState({
                'totalWeight': value
            })
        } else {
            return false;
        }
    }

    _onTotalVolumeChange = (event) => {
        let value = event.target.value;
        value = value.replace(/,/g, ".");

        if (this._validateFloat(value)) {
            this.setState({
                'totalVolume': value
            })
        } else {
            return false;
        }
    }

    _validateFloat = (value) => {
        if ((Regexps['FLOAT'].test(value) && value < Number.MAX_SAFE_INTEGER) || !value) {
            return true;
        }

        return false;
    }

    _changeParameters = () => {
        ga.event({
            category: 'buttons',
            action: 'change_overall_weight_vol_step_2',
            label: this.props.selectedCategory.name,
            value: 1,
        });

        ActionCreators.changeTotalMeasurements(true);

        if (this.totalVolumeInput) {
            this.totalVolumeInput.focus();
        }
    }

    _saveParameters = () => {
        ga.event({
            category: 'buttons',
            action: 'save_new_weight_vol_step_2',
            label: this.props.selectedCategory.name,
            value: 1,
        });

        let totalWeight = this.state.totalWeight;
        const totalVolume = this.state.totalVolume;

        if (this.state.weightMeasure === TEXT.WEIGHT[1]) {
            totalWeight *= 1000; // t => kg
        }

        ActionCreators.setTotalMeasurements(totalWeight, totalVolume);
    }

    _cancelChangeParameters = () => {
        ga.event({
            category: 'buttons',
            action: 'return_base_weight_vol_step_2',
            label: this.props.selectedCategory.name,
            value: 1,
        });

        this.setState({
            'totalVolume': this.state.totalMeasurements.get('total_volume'),
            'totalWeight': this.state.totalMeasurements.get('total_weight')
        });

        ActionCreators.changeTotalMeasurements(false);
    }

    _onTotalVolumeBlur = () => {
        if (this.state.totalMeasurements.get('is_changeable')) {
            ga.event({
                category: 'fields',
                action: 'overall_vol_fill_step_2',
                label: this.props.selectedCategory.name,
                value: 1,
            });
        }
    }

    _onTotalWeightBlur = () => {
        if (this.state.totalMeasurements.get('is_changeable')) {
            ga.event({
                category: 'fields',
                action: 'overall_weight_fill_step_2',
                label: this.props.selectedCategory.name,
                value: 1,
            });
        }
    }

    _onTotalVolumeClick = () => {
        if (!this.state.totalMeasurements.get('is_changeable')) {
            ga.event({
                category: 'fields',
                action: 'vol_fill_false_click',
                label: this.props.selectedCategory.name,
                value: 1,
            });
        }
    }

    _onTotalWeightClick = () => {
        if (!this.state.totalMeasurements.get('is_changeable')) {
            ga.event({
                category: 'fields',
                action: 'weight_fill_false_click',
                label: this.props.selectedCategory.name,
                value: 1,
            });
        }
    }

    _renderParameters = () => {
        const isReadOnly = !this.state.totalMeasurements.get('is_changeable');

        return (
            <div className="TotalMeasurements__parameters">
                <div className="TotalMeasurements__parameter">
                    <InputControl className="TotalMeasurements__input-control"
                        inputClassName="TotalMeasurements__input"
                        inputRef={this._initTotalVolumeInputRef}
                        onChange={this._onTotalVolumeChange}
                        onBlur={this._onTotalVolumeBlur}
                        onClick={this._onTotalVolumeClick}
                        value={this.state.totalVolume}
                        label={TEXT.TOTAL_VOLUME}
                        readOnly={isReadOnly} />
                    <span className="TotalMeasurements__measure">{TEXT.VOLUME[1]}</span>
                </div>
                <div className="TotalMeasurements__parameter">
                    <InputControl className="TotalMeasurements__input-control"
                        inputClassName="TotalMeasurements__input"
                        onChange={this._onTotalWeightChange}
                        onBlur={this._onTotalWeightBlur}
                        onClick={this._onTotalWeightClick}
                        value={this.state.totalWeight}
                        label={TEXT.TOTAL_WEIGHT}
                        readOnly={isReadOnly} />
                    <span className="TotalMeasurements__measure">{this.state.weightMeasure}</span>
                </div>
            </div>
        );
    }

    _renderButtons = () => {
        if (this.state.totalMeasurements.get('is_changeable')) {
            return (
                <div className="TotalMeasurements__buttons">
                    <ButtonControl className="TotalMeasurements__button"
                        view="forest"
                        onClick={this._saveParameters}>
                        {TEXT.SAVE}
                    </ButtonControl>
                    <ButtonControl className="TotalMeasurements__button"
                        view="blackLink"
                        onClick={this._cancelChangeParameters}>
                        {TEXT.CANCEL}
                    </ButtonControl>
                </div>
            )
        } else {
            return (
                <div className="TotalMeasurements__buttons">
                    <ButtonControl
                        view="darkStone"
                        onClick={this._changeParameters}>
                        Изменить общие объём и&nbsp;вес
                    </ButtonControl>
                </div>
            )
        }
    }

    _returnTotalMeasurementsTitle = () => {
        if (this.props.selectedCategory.name === 'book_separate_car') {
            return (
                <span>Укажите общий вес и&nbsp;объем грузов в&nbsp;заявке</span>
            );
        } else {
            return (
                <span>Общий вес и&nbsp;объем грузов в&nbsp;заявке</span>
            );
        }
    }

    _returnTotalMeasurementsText = () => {
        if (this.props.selectedCategory.name === 'book_separate_car') {
            return (
                <span>Обязательно проверьте соответствуют&nbsp;ли параметры вашего груза этому типу грузовика, на&nbsp;основе этих значений перевозчики будут рассчитывать стоимость перевозки.<br />Если габариты и&nbsp;вес отдельных грузов неизвестны или общий вес и&nbsp;объем рассчитаны неверно, вы&nbsp;можете скорректировать их&nbsp;или выбрать более подходящий тип грузовика.</span>
            );
        } else {
            return (
                <span>Обязательно проверьте указанные значения, на&nbsp;основе этих значений перевозчики будут рассчитывать стоимость перевозки. Если габариты и&nbsp;вес отдельных грузов неизвестны или общий вес и&nbsp;объем рассчитаны неверно, вы&nbsp;можете скорректировать&nbsp;их. После этого автоматический подсчёт общего объёма и&nbsp;веса будет отключён.</span>
            );
        }
    }

    _renderTotalMeasurementsAlert = () => {
        if (this.props.selectedCategory.name === 'book_separate_car'
            && this.state.totalMeasurements.get('is_auto_total_measurements_count_on')
            && (this.state.totalMeasurements.get('total_volume') > 0)
            && (this.state.totalMeasurements.get('total_weight') > 0)) {
            return (
                <div className="TotalMeasurements__alert">Указаны максимальные параметры груза, которые может перевести выбранный тип грузовика</div>
            )
        } else {
            return null;
        }
    }

    render() {
        const parametersBlock = this._renderParameters();
        const buttonsBlock = this._renderButtons();
        const totalMeasurementsTitle = this._returnTotalMeasurementsTitle();
        const totalMeasurementsText = this._returnTotalMeasurementsText();
        const totalMeasurementsAlert = this._renderTotalMeasurementsAlert();

        return (
            <div className="TotalMeasurements">
                <div className="TotalMeasurements__parameters-block">
                    {parametersBlock}
                    {buttonsBlock}
                </div>
                <div className="TotalMeasurements__promo">
                    <div className="TotalMeasurements__title">{totalMeasurementsTitle}</div>
                    {totalMeasurementsAlert}
                    <div className="TotalMeasurements__text">{totalMeasurementsText}</div>
                </div>
            </div>
        );
    }
}

export default TotalMeasurementsPaneControl;



// WEBPACK FOOTER //
// ./client/components/TotalMeasurementsPaneControl/TotalMeasurementsPaneControl.js